import React from 'react';
import { Box, Typography, Card, CardContent, useTheme, useMediaQuery } from '@mui/material';
import { School, Work } from '@mui/icons-material';
import Timeline from '@mui/lab/Timeline';
import TimelineItem from '@mui/lab/TimelineItem';
import TimelineSeparator from '@mui/lab/TimelineSeparator';
import TimelineConnector from '@mui/lab/TimelineConnector';
import TimelineContent from '@mui/lab/TimelineContent';
import TimelineOppositeContent from '@mui/lab/TimelineOppositeContent';
import TimelineDot from '@mui/lab/TimelineDot';

const EducationAndWorkTimeline = () => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

    const cardStyle = {
        backgroundColor: '#f4f4f4',
        maxWidth: '100%',
        margin: '0 auto',
        boxShadow: '0 4px 8px rgba(0,0,0,0.2)',
    };
    const textStyle = { fontSize: isMobile ? '14px' : undefined };

    const timelineItemStyle = {
        width: '100%',
    };

    const oppositeContentStyle = {
        display: isMobile ? 'none' : 'block',
        flex: 1,
    };

    return (
        <Box sx={{ p: 4, backgroundColor: 'white' }}>
            <Typography variant="h4" gutterBottom component="div" sx={{
                fontWeight: 'bold',
                textAlign: 'center',
                fontSize: isMobile ? '30px' : '50px',
                color: 'black',
                marginBottom: '30px'
            }}>
                Education <span style={{ color: '#f9004d' }}>&</span> Work Experience
            </Typography>

            <Timeline position={isMobile ? "right" : "alternate"}>

                {/* 1. Guardian's Embrace (left) */}
                <TimelineItem sx={timelineItemStyle}>
                    <TimelineOppositeContent sx={oppositeContentStyle} />
                    <TimelineSeparator>
                        <TimelineDot color="primary">
                            <Work />
                        </TimelineDot>
                        <TimelineConnector style={{ backgroundColor: 'black' }} />
                    </TimelineSeparator>
                    <TimelineContent sx={{ py: '12px', px: isMobile ? 1 : 2 }}>
                        <Card sx={cardStyle}>
                            <CardContent>
                                <Typography variant="h5" gutterBottom fontWeight={'bold'}>Guardian's Embrace</Typography>
                                <Typography sx={textStyle}>
                                    <strong>Software Engineer</strong> (Full-time, Remote) <br/>
                                    April 2026 - Present <br/><br/>
                                    • Onboarded 200+ volunteers by owning end-to-end delivery of a full-stack portal built in React, TypeScript, and Node.js.<br/>
                                    • Contributing to a Gen AI donor assistant using RAG over PostgreSQL program data for natural-language donation Q&A.
                                </Typography>
                            </CardContent>
                        </Card>
                    </TimelineContent>
                </TimelineItem>

                {/* 2. RSO Proctor (right) */}
                <TimelineItem sx={timelineItemStyle}>
                    <TimelineOppositeContent sx={oppositeContentStyle} />
                    <TimelineSeparator>
                        <TimelineDot color="primary">
                            <Work />
                        </TimelineDot>
                        <TimelineConnector style={{ backgroundColor: 'black' }} />
                    </TimelineSeparator>
                    <TimelineContent sx={{ py: '12px', px: isMobile ? 1 : 2 }}>
                        <Card sx={cardStyle}>
                            <CardContent>
                                <Typography variant="h5" gutterBottom fontWeight={'bold'}>Residential Security Office (Northeastern University)</Typography>
                                <Typography sx={textStyle}>
                                    <strong>RSO Proctor</strong> (Part-time | On-Campus @ NEU) <br/>
                                    June 2024 - December 2025 <br/> <br/>
                                    • Responsible for providing access control at Northeastern University Residence halls. <br/>
                                    • Responsible for providing security to residents and providing basic campus information. <br/>
                                    • Regularly interact with Residential Life, Building services, and NUPD for ensuring a safe environment around residence halls.
                                </Typography>
                            </CardContent>
                        </Card>
                    </TimelineContent>
                </TimelineItem>

                {/* 3. Northeastern (left) */}
                <TimelineItem sx={timelineItemStyle}>
                    <TimelineOppositeContent sx={oppositeContentStyle} />
                    <TimelineSeparator>
                        <TimelineDot color="secondary">
                            <School />
                        </TimelineDot>
                        <TimelineConnector style={{ backgroundColor: 'black' }} />
                    </TimelineSeparator>
                    <TimelineContent sx={{ py: '12px', px: isMobile ? 1 : 2 }}>
                        <Card sx={cardStyle}>
                            <CardContent>
                                <Typography variant="h5" gutterBottom fontWeight={'bold'}>Northeastern University</Typography>
                                <Typography sx={textStyle}>
                                    <strong>Master of Science in Information Systems</strong> (Full-time) <br/>
                                    January 2024 - December 2025 <br/>
                                    • GPA: 3.70/4.0 <br/><br/>
                                    Completed my Master's degree in Information Systems from Northeastern University, Boston, MA.
                                </Typography>
                            </CardContent>
                        </Card>
                    </TimelineContent>
                </TimelineItem>

                {/* 4. Wipro (right) */}
                <TimelineItem sx={timelineItemStyle}>
                    <TimelineOppositeContent sx={oppositeContentStyle} />
                    <TimelineSeparator>
                        <TimelineDot color="primary">
                            <Work />
                        </TimelineDot>
                        <TimelineConnector style={{ backgroundColor: 'black' }} />
                    </TimelineSeparator>
                    <TimelineContent sx={{ py: '12px', px: isMobile ? 1 : 2 }}>
                        <Card sx={cardStyle}>
                            <CardContent>
                                <Typography variant="h5" gutterBottom fontWeight={'bold'}>Wipro Limited</Typography>
                                <Typography sx={textStyle}>
                                    <strong>Software Engineer</strong> (Full-time)<br/>
                                    October 2021 - November 2023 <br/><br/>
                                    Worked as a Software Engineer at Wipro Ltd for 2 years with strong expertise in Python, C/C++, DevOps automation (Jenkins, Docker, Kubernetes), AWS, and CI/CD pipelines.<br/>
                                    Earned multiple certifications in NetApp storage engineering during this time.
                                </Typography>
                            </CardContent>
                        </Card>
                    </TimelineContent>
                </TimelineItem>

                {/* 5. Mumbai University (left) */}
                <TimelineItem sx={timelineItemStyle}>
                    <TimelineOppositeContent sx={oppositeContentStyle} />
                    <TimelineSeparator>
                        <TimelineDot color="secondary">
                            <School />
                        </TimelineDot>
                    </TimelineSeparator>
                    <TimelineContent sx={{ py: '12px', px: isMobile ? 1 : 2 }}>
                        <Card sx={cardStyle}>
                            <CardContent>
                                <Typography variant="h5" gutterBottom fontWeight={'bold'}>Mumbai University</Typography>
                                <Typography sx={textStyle}>
                                    <strong>Bachelor's of Science in Information Technology</strong> (Full-time)<br/>
                                    June 2018 - May 2021 <br/>
                                    • GPA: 3.8/4.0 <br/><br/>
                                    Completed my Bachelor's degree in Information Technology in May 2021 from Mumbai University, India.
                                </Typography>
                            </CardContent>
                        </Card>
                    </TimelineContent>
                </TimelineItem>

            </Timeline>
        </Box>
    );
};

export default EducationAndWorkTimeline;